import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ContactMethods from './components/ContactMethods';
import ContactForm from './components/ContactForm';
import ProjectScopeVisualizer from './components/ProjectScopeVisualizer';
import SuccessConfirmation from './components/SuccessConfirmation';
import FloatingParticles from './components/FloatingParticles';

const ContactNexus = () => {
  const [currentView, setCurrentView] = useState('methods'); // methods, form, visualizer, success
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [formData, setFormData] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [projectScope, setProjectScope] = useState(null);

  // Handle method selection
  const handleMethodSelect = (methodId) => {
    setSelectedMethod(methodId);
    
    if (methodId === 'project') {
      setCurrentView('visualizer');
    } else {
      setCurrentView('form');
    }
  };

  // Handle scope completion
  const handleScopeComplete = (scope) => {
    setProjectScope(scope);
    setCurrentView('form');
  };

  // Handle form submission
  const handleFormSubmit = async (data) => {
    setIsSubmitting(true);
    setFormData(data);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    setIsSubmitting(false);
    setCurrentView('success');
  };

  // Reset to initial state
  const handleReset = () => {
    setCurrentView('methods');
    setSelectedMethod(null);
    setFormData(null);
    setProjectScope(null);
  };

  // Auto-scroll to top on view change
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentView]);

  return (
    <>
      <Helmet>
        <title>Contact Nexus - 3D Portfolio Showcase</title>
        <meta name="description" content="Connect with me for your next creative project. Interactive contact experience with project scope visualization and real-time availability." />
        <meta name="keywords" content="contact, project inquiry, collaboration, 3D web development, creative services" />
      </Helmet>
      <div className="min-h-screen bg-background relative overflow-hidden">
        <Header />
        <FloatingParticles />

        {/* Hero Section */}
        <section className="relative pt-24 pb-16">
          <div className="container mx-auto px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center mb-16"
            >
              <div className="flex items-center justify-center space-x-4 mb-6">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
                  className="w-16 h-16 bg-gradient-to-br from-accent to-cosmic-energy rounded-2xl flex items-center justify-center cosmic-glow"
                >
                  <Icon name="Zap" size={32} className="text-background" />
                </motion.div>
                <div className="text-left">
                  <h1 className="font-headline text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-2">
                    Contact Nexus
                  </h1>
                  <p className="font-body text-lg text-muted-foreground">
                    Your gateway to creative collaboration
                  </p>
                </div>
              </div>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="font-body text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed"
              >
                Ready to transform your ideas into immersive digital experiences? 
                Let's start a conversation that could change everything.
              </motion.p>
            </motion.div>

            {/* Dynamic Content Area */}
            <div className="relative z-10">
              <AnimatePresence mode="wait">
                {currentView === 'methods' && (
                  <motion.div
                    key="methods"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3 }}
                  >
                    <ContactMethods onMethodSelect={handleMethodSelect} />
                  </motion.div>
                )}

                {currentView === 'visualizer' && (
                  <motion.div
                    key="visualizer"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3 }}
                  >
                    <div className="mb-6">
                      <Button
                        variant="ghost"
                        onClick={handleReset}
                        iconName="ArrowLeft"
                        iconPosition="left"
                        className="text-muted-foreground hover:text-foreground"
                      >
                        Back to Contact Methods
                      </Button>
                    </div>
                    <ProjectScopeVisualizer onScopeComplete={handleScopeComplete} />
                  </motion.div>
                )}

                {currentView === 'form' && (
                  <motion.div
                    key="form"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3 }}
                  >
                    <div className="mb-6">
                      <Button
                        variant="ghost"
                        onClick={handleReset}
                        iconName="ArrowLeft"
                        iconPosition="left"
                        className="text-muted-foreground hover:text-foreground"
                      >
                        Back to Contact Methods
                      </Button>
                    </div>
                    
                    {projectScope && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="glass-panel-light rounded-lg p-4 mb-6"
                      >
                        <div className="flex items-center space-x-3">
                          <Icon name="Calculator" size={20} className="text-accent" />
                          <div>
                            <p className="font-body text-sm text-foreground">
                              Project Estimate: <span className="text-accent font-medium">
                                ${(projectScope?.estimate?.totalPrice * 0.8)?.toLocaleString()} - ${(projectScope?.estimate?.totalPrice * 1.2)?.toLocaleString()}
                              </span>
                            </p>
                            <p className="font-body text-xs text-muted-foreground">
                              Timeline: {projectScope?.estimate?.timeline}
                            </p>
                          </div>
                        </div>
                      </motion.div>
                    )}
                    
                    <ContactForm
                      selectedService={selectedMethod === 'project' ? 'web-development' : ''}
                      onSubmit={handleFormSubmit}
                      isSubmitting={isSubmitting}
                    />
                  </motion.div>
                )}

                {currentView === 'success' && formData && (
                  <motion.div
                    key="success"
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    transition={{ duration: 0.3 }}
                  >
                    <SuccessConfirmation
                      formData={formData}
                      onReset={handleReset}
                    />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </section>

        {/* Additional Information Section */}
        <section className="relative py-16 border-t border-border/20">
          <div className="container mx-auto px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="w-12 h-12 bg-gradient-to-br from-accent to-blue-400 rounded-lg flex items-center justify-center mx-auto mb-4 cosmic-glow">
                  <Icon name="Clock" size={24} className="text-white" />
                </div>
                <h3 className="font-headline text-lg text-foreground mb-2">Quick Response</h3>
                <p className="font-body text-sm text-muted-foreground">
                  I typically respond to project inquiries within 4-6 hours during business days.
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="text-center"
              >
                <div className="w-12 h-12 bg-gradient-to-br from-cosmic-energy to-orange-400 rounded-lg flex items-center justify-center mx-auto mb-4 cosmic-glow">
                  <Icon name="Shield" size={24} className="text-white" />
                </div>
                <h3 className="font-headline text-lg text-foreground mb-2">Confidential</h3>
                <p className="font-body text-sm text-muted-foreground">
                  All project discussions are kept strictly confidential. NDAs available upon request.
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="text-center"
              >
                <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center mx-auto mb-4 cosmic-glow">
                  <Icon name="Globe" size={24} className="text-white" />
                </div>
                <h3 className="font-headline text-lg text-foreground mb-2">Global Reach</h3>
                <p className="font-body text-sm text-muted-foreground">
                  Working with clients worldwide across all time zones. Remote collaboration is my specialty.
                </p>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Background Elements */}
        <div className="fixed inset-0 pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-accent/5 rounded-full blur-3xl"></div>
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-cosmic-energy/5 rounded-full blur-3xl"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-128 h-128 bg-purple-500/5 rounded-full blur-3xl"></div>
        </div>
      </div>
    </>
  );
};

export default ContactNexus;